import { useCallback, useMemo } from 'react'

import {
  useActiveAccount,
  useActiveWallet,
  useActiveWalletChain,
  useConnectModal,
  useDisconnect,
  useSwitchActiveWalletChain,
} from 'thirdweb/react'

import Client from '../client'
import { CHAIN_IDS_TO_CHAINS, SUPPORTED_CHAINS } from '../constants/chain'
import { supportedWallets } from '../constants/wallet'

import type { SupportedChainId } from '../constants/chain'

/**
 * Returns a list of states/actions available from the Wallet.
 */
export const useConnectWallet = () => {
  const { connect: connectWallet, isConnecting } = useConnectModal()
  const { disconnect: disconnectWallet } = useDisconnect()
  const account = useActiveAccount()
  const wallet = useActiveWallet()
  const activeChain = useActiveWalletChain()
  const switchActiveWalletChain = useSwitchActiveWalletChain()
  const client = Client.instance

  const address = useMemo(() => {
    return account?.address ?? ''
  }, [account])

  const connect = useCallback(async () => {
    if (client != null) {
      await connectWallet({
        client,
        wallets: supportedWallets,
        size: 'compact',
        showThirdwebBranding: false,
        chains: SUPPORTED_CHAINS,
      })
    }
  }, [client, connectWallet])

  const disconnect = useCallback(() => {
    if (!wallet) return
    disconnectWallet(wallet)
  }, [wallet, disconnectWallet])

  const isConnected = useMemo(() => {
    return Boolean(address != null && address !== '')
  }, [address])

  const switchChain = useCallback(
    async (chainId: SupportedChainId) => {
      await switchActiveWalletChain(CHAIN_IDS_TO_CHAINS[chainId])
    },
    [switchActiveWalletChain],
  )

  return useMemo(() => {
    return {
      account,
      activeChain,
      address,
      connect,
      disconnect,
      isConnected,
      isConnecting,
      switchChain,
      wallet,
    }
  }, [
    account,
    activeChain,
    address,
    connect,
    disconnect,
    isConnected,
    isConnecting,
    switchChain,
    wallet,
  ])
}
